import { baseApi } from "./baseApi";
import type {
  Deal,
  DealsBoardParams,
  DealDetail,
  DealsBoardResponse,
  DealsListParams,
  DealsListResponse,
  DealSearchResponse,
  DealsViewPayload,
  DealsViewItem,
  DealStageConfig,
  DealMetricsResponse,
  DealChangeLog,
} from "../../pages/Crm/components/Deals/utils/types";

export const dealsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDealsBoard: builder.query<DealsBoardResponse, DealsBoardParams | void>({
      query: (params) => {
        const { assignee, is_unassigned, ...rest } = (params ||
          {}) as DealsBoardParams;
        const searchParams = new URLSearchParams();
        Object.entries(rest).forEach(([key, val]) => {
          if (val != null && val !== "") searchParams.append(key, String(val));
        });
        assignee?.forEach((id) => searchParams.append("assignee", id));
        if (is_unassigned) searchParams.append("unassigned", "true");
        const qs = searchParams.toString();
        return { url: `/deals/board${qs ? `?${qs}` : ""}`, method: "GET" };
      },
    }),

    getDealsList: builder.query<DealsListResponse, DealsListParams>({
      query: (params) => {
        const { assignee, is_unassigned, ...rest } = params;
        const searchParams = new URLSearchParams();
        Object.entries(rest).forEach(([key, val]) => {
          if (val != null && val !== "") searchParams.append(key, String(val));
        });
        assignee?.forEach((id) => searchParams.append("assignee", id));
        if (is_unassigned) searchParams.append("unassigned", "true");
        const qs = searchParams.toString();
        return { url: `/deals${qs ? `?${qs}` : ""}`, method: "GET" };
      },
    }),

    getDealStages: builder.query<DealStageConfig[], void>({
      query: () => ({
        url: "/deals/stages",
        method: "GET",
      }),
    }),

    getDealById: builder.query<DealDetail, string>({
      query: (dealId) => ({
        url: `/deals/${dealId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, dealId) => [
        { type: "Deal", id: dealId },
      ],
    }),

    dealSearch: builder.mutation<DealSearchResponse, string>({
      query: (query) => ({
        url: `/deals/search?search_query=${query}`,
        method: "GET",
      }),
    }),

    getDealsViews: builder.query<
      { views: DealsViewItem[] },
      { page?: number; limit?: number; search?: string } | void
    >({
      query: (params) => ({
        url: "/deals/views",
        method: "GET",
        params: params || undefined,
      }),
    }),

    createDealsView: builder.mutation<
      { id: string; created_at: string; updated_at: string },
      DealsViewPayload
    >({
      query: (payload) => ({
        url: "/deals/views",
        method: "POST",
        body: payload,
      }),
    }),

    updateDealsView: builder.mutation<
      { updated_at: string },
      { viewId: string } & Partial<DealsViewPayload>
    >({
      query: ({ viewId, ...body }) => ({
        url: `/deals/views/${viewId}`,
        method: "PATCH",
        body,
      }),
    }),

    deleteDealsView: builder.mutation<void, string>({
      query: (viewId) => ({
        url: `/deals/views/${viewId}`,
        method: "DELETE",
      }),
    }),

    updateDeal: builder.mutation<
      Deal,
      { contractId: string; body: { assignee?: string | null } }
    >({
      query: ({ contractId, body }) => ({
        url: `/deals/${contractId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: "Deal", id: contractId },
      ],
    }),

    getDealCancellationReasons: builder.query<
      {
        primary_reasons: string[];
        mapping: Record<string, string[]>;
      },
      void
    >({
      query: () => ({
        url: "/deals/cancellation-reasons",
        method: "GET",
      }),
    }),

    updateDealCancellationReasons: builder.mutation<
      Deal,
      {
        dealId: string;
        body: {
          cancelled_primary_reason?: string | null;
          cancelled_reason?: string | null;
          cancellation_reason_text?: string | null;
        };
      }
    >({
      query: ({ dealId, body }) => ({
        url: `/deals/${dealId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { dealId }) => [
        { type: "Deal", id: dealId },
        { type: "DealChanges" },
      ],
    }),

    getDealDistinctCities: builder.query<{ values: string[] }, void>({
      query: () => ({
        url: "/deals/distinct/cities",
        method: "GET",
      }),
    }),

    getDealDistinctAvailabilityTitles: builder.query<
      { values: string[] },
      void
    >({
      query: () => ({
        url: "/deals/distinct/availability-titles",
        method: "GET",
      }),
    }),

    getDealDistinctAcademicYears: builder.query<{ values: string[] }, void>({
      query: () => ({
        url: "/deals/distinct/academic-years",
        method: "GET",
      }),
    }),

    getDealDistinctRoomTypes: builder.query<{ values: string[] }, void>({
      query: () => ({
        url: "/deals/distinct/room-types",
        method: "GET",
      }),
    }),

    getDealsMetrics: builder.query<
      DealMetricsResponse,
      { view_id?: string } | void
    >({
      query: (params) => ({
        url: "/deals/metrics",
        method: "GET",
        params: params || undefined,
      }),
    }),

    getDealChanges: builder.query<DealChangeLog[], string>({
      query: (signatureId) => ({
        url: `/deals/${signatureId}/changes`,
        method: "GET",
      }),
      providesTags: (_result, _error, signatureId) => [
        { type: "DealChanges", id: signatureId },
      ],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDealsBoardQuery,
  useLazyGetDealsBoardQuery,
  useGetDealsListQuery,
  useLazyGetDealsListQuery,
  useGetDealByIdQuery,
  useGetDealStagesQuery,
  useDealSearchMutation,
  useGetDealsViewsQuery,
  useCreateDealsViewMutation,
  useUpdateDealsViewMutation,
  useDeleteDealsViewMutation,
  useUpdateDealMutation,
  useGetDealCancellationReasonsQuery,
  useUpdateDealCancellationReasonsMutation,
  useGetDealDistinctCitiesQuery,
  useGetDealDistinctAvailabilityTitlesQuery,
  useGetDealDistinctAcademicYearsQuery,
  useGetDealDistinctRoomTypesQuery,
  useGetDealsMetricsQuery,
  useGetDealChangesQuery,
} = dealsApi;
